import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { siteBuilderAPI, mediaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FiPackage, FiPlus, FiEdit2, FiTrash2, FiCopy, FiSettings,
  FiDollarSign, FiClock, FiList, FiCreditCard, FiTruck,
  FiCalendar, FiPercent, FiCheck, FiX, FiSave, FiImage,
  FiChevronDown, FiChevronUp, FiGripVertical, FiRefreshCw,
  FiUpload, FiFolder
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default booking form fields
const DEFAULT_FORM_FIELDS = [
  { field_type: 'text', label: 'Prénom', required: true },
  { field_type: 'text', label: 'Nom', required: true },
  { field_type: 'email', label: 'Email', required: true },
  { field_type: 'phone', label: 'Téléphone', required: true },
  { field_type: 'date', label: 'Date de l\'événement', required: true },
  { field_type: 'select', label: 'Type d\'événement', required: true, options: ['Mariage', 'Anniversaire', 'Baptême', 'Entreprise', 'Soirée privée', 'Autre'] },
  { field_type: 'textarea', label: 'Adresse de l\'événement', required: true },
  { field_type: 'textarea', label: 'Message', required: false },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Texte court' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'number', label: 'Nombre' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Heure' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'checkbox', label: 'Case à cocher' },
];

export default function ProductsManagementPage() {
  const [products, setProducts] = useState([]);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    base_price: 0,
    images: [],
    cover_image: '', // Image for mega menu
    presentation_video_id: '', // YouTube video ID for presentation page background
    category: '',
    is_active: true,
    is_featured: false,
    variations: [],
    options: [],
    booking_form_fields: [],
    deposit_config: { deposit_type: 'percentage', deposit_value: 30, deposit_required: true },
    payment_config: {
      payment_methods: ['bank_transfer', 'card'],
      bank_transfer_enabled: true,
      cash_enabled: true,
      mypos_enabled: false,
      stripe_enabled: false,
      payment_deadline_days: 7
    },
    conditions: {
      min_advance_days: 3,
      max_advance_days: 365,
      free_delivery_radius_km: 30,
      delivery_fee_per_km: 1.5,
      available_days: [0, 1, 2, 3, 4, 5, 6]
    },
    formulas: []  // Product bundles/combinations with discounts
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load site
      const siteResponse = await siteBuilderAPI.getMySite();
      setSite(siteResponse.data);
      
      // Load products from WooCommerce API (which also handles local MongoDB products)
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/woocommerce/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Transform WooCommerce products to match our format
        const transformedProducts = data.map(p => ({
          ...p,
          base_price: p.price || p.regular_price || 0,
          active: p.status === 'publish',
          product_type: p.type === 'variable' ? 'rental' : 'service',
          variations: p.variations_data || p.variations || []
        }));
        setProducts(transformedProducts);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const loadMediaLibrary = async () => {
    setLoadingMedia(true);
    try {
      const response = await mediaAPI.getAll();
      const media = response.data?.media || [];
      // Filter only images
      const images = media.filter(m => m.type === 'image' || m.content_type?.startsWith('image/'));
      setMediaLibrary(images);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Erreur lors du chargement de la médiathèque');
    } finally {
      setLoadingMedia(false);
    }
  };

  const selectMediaForProduct = (mediaItem) => {
    const mediaUrl = mediaItem.url || mediaItem.file_url;
    const fullUrl = mediaUrl?.startsWith('http') ? mediaUrl : `${API_URL}${mediaUrl}`;
    setFormData(prev => ({ ...prev, images: [fullUrl] }));
    setShowMediaPicker(false);
    toast.success('Image sélectionnée');
  };

  const handleInitDefaults = async () => {
    if (!site?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/products/init-defaults?site_id=${site.id}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const openCreateDialog = () => {
    setSelectedProduct(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      base_price: 0,
      images: [],
      category: '',
      is_active: true,
      is_featured: false,
      variations: [],
      options: [],
      booking_form_fields: DEFAULT_FORM_FIELDS.map((f, i) => ({ ...f, id: `field-${i}`, order: i })),
      formulas: [],
      deposit_config: { deposit_type: 'percentage', deposit_value: 30, deposit_required: true },
      payment_config: {
        payment_methods: ['bank_transfer', 'card'],
        bank_transfer_enabled: true,
        cash_enabled: true,
        payment_deadline_days: 7
      },
      conditions: {
        min_advance_days: 3,
        max_advance_days: 365,
        free_delivery_radius_km: 30,
        delivery_fee_per_km: 1.5,
        available_days: [0, 1, 2, 3, 4, 5, 6]
      }
    });
    setActiveTab('general');
    setEditDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      ...product,
      variations: product.variations || [],
      options: product.options || [],
      booking_form_fields: product.booking_form_fields || [],
      formulas: product.formulas || [],
      deposit_config: product.deposit_config || { deposit_type: 'percentage', deposit_value: 30, deposit_required: true },
      payment_config: product.payment_config || { payment_methods: ['bank_transfer'], bank_transfer_enabled: true, payment_deadline_days: 7 },
      conditions: product.conditions || { min_advance_days: 3, free_delivery_radius_km: 30, delivery_fee_per_km: 1.5 }
    });
    setActiveTab('general');
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Le nom du produit est requis');
      return;
    }

    setSaving(true);
    try {
      if (selectedProduct) {
        // Update
        const response = await fetch(`${API_URL}/api/products/${selectedProduct.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (response.ok) {
          toast.success('Produit mis à jour');
        }
      } else {
        // Create
        const response = await fetch(`${API_URL}/api/products/?site_id=${site.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (response.ok) {
          toast.success('Produit créé');
        }
      }
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Produit supprimé');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}/duplicate`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Produit dupliqué');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  // Variations management
  const addVariation = () => {
    setFormData(prev => ({
      ...prev,
      variations: [...prev.variations, {
        id: `var-${Date.now()}`,
        name: '',
        price: prev.base_price || 0,
        duration_hours: null,
        is_active: true
      }]
    }));
  };

  const updateVariation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const removeVariation = (index) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  // Options management
  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, {
        id: `opt-${Date.now()}`,
        name: '',
        price: 0,
        description: '',
        is_included: false,
        is_active: true
      }]
    }));
  };

  const updateOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((o, i) => i === index ? { ...o, [field]: value } : o)
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Form fields management
  const addFormField = () => {
    setFormData(prev => ({
      ...prev,
      booking_form_fields: [...prev.booking_form_fields, {
        id: `field-${Date.now()}`,
        field_type: 'text',
        label: '',
        placeholder: '',
        required: false,
        options: [],
        order: prev.booking_form_fields.length
      }]
    }));
  };

  const updateFormField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      booking_form_fields: prev.booking_form_fields.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const removeFormField = (index) => {
    setFormData(prev => ({
      ...prev,
      booking_form_fields: prev.booking_form_fields.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 bg-white rounded-xl p-6">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Gestion des Produits</h1>
          <p className="text-sm text-slate-500">Gérez vos prestations, variations, options et formulaires</p>
        </div>
        <div className="flex gap-2">
          {products.length === 0 && (
            <Button variant="outline" size="sm" onClick={handleInitDefaults} className="text-xs md:text-sm">
              <FiPackage className="mr-1 md:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Produits par défaut</span>
              <span className="sm:hidden">Défaut</span>
            </Button>
          )}
          <Button onClick={openCreateDialog} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs md:text-sm">
            <FiPlus className="mr-1 md:mr-2 h-4 w-4" />
            Nouveau produit
          </Button>
        </div>
      </div>

      {/* Products Grid - Responsive */}
      {products.length === 0 ? (
        <Card>
          <CardContent className="py-8 md:py-12 text-center">
            <FiPackage className="h-10 w-10 md:h-12 md:w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-base md:text-lg font-medium text-slate-900 mb-2">Aucun produit</h3>
            <p className="text-sm text-slate-500 mb-4">Créez votre premier produit ou initialisez les produits par défaut</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button variant="outline" size="sm" onClick={handleInitDefaults}>
                Produits par défaut
              </Button>
              <Button size="sm" onClick={openCreateDialog}>
                <FiPlus className="mr-2" />
                Créer un produit
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  {/* Product Image */}
                  {product.images?.[0] && (
                    <div className="aspect-video bg-slate-100 relative">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                      {product.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-yellow-500">Vedette</Badge>
                      )}
                      {!product.is_active && (
                        <Badge variant="secondary" className="absolute top-2 right-2">Inactif</Badge>
                      )}
                    </div>
                  )}
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">{product.short_description}</p>
                    
                    {/* Stats */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="outline" className="text-xs">
                        <FiDollarSign className="mr-1 h-3 w-3" />
                        À partir de {product.base_price}€
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <FiList className="mr-1 h-3 w-3" />
                        {product.variations?.length || 0} variations
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <FiSettings className="mr-1 h-3 w-3" />
                        {product.options?.length || 0} options
                      </Badge>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(product)}
                      >
                        <FiEdit2 className="mr-1 h-4 w-4" />
                        Modifier
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(product.id)}
                      >
                        <FiCopy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(product.id)}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedProduct ? `Modifier: ${selectedProduct.name}` : 'Nouveau produit'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-7 w-full">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="variations">Variations</TabsTrigger>
              <TabsTrigger value="options">Options</TabsTrigger>
              <TabsTrigger value="formulas">Formules</TabsTrigger>
              <TabsTrigger value="form">Formulaire</TabsTrigger>
              <TabsTrigger value="deposit">Acompte</TabsTrigger>
              <TabsTrigger value="conditions">Conditions</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-y-auto py-4">
              {/* General Tab */}
              <TabsContent value="general" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du produit *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Location PhotoBooth"
                    />
                  </div>
                  <div>
                    <Label>Catégorie</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="photobooth"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Description courte</Label>
                  <Input
                    value={formData.short_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                    placeholder="Photobooth professionnel avec impressions illimitées"
                  />
                </div>
                
                <div>
                  <Label>Description complète</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prix de base (€)</Label>
                    <Input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>Image du produit</Label>
                    <div className="space-y-3">
                      {/* Preview de l'image actuelle */}
                      {formData.images?.[0] && (
                        <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-slate-100 border">
                          <img 
                            src={formData.images[0]} 
                            alt="Aperçu" 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, images: [] }))}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <FiX className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      
                      {/* Boutons de sélection */}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowMediaPicker(true);
                            loadMediaLibrary();
                          }}
                          className="flex items-center gap-2"
                        >
                          <FiFolder className="h-4 w-4" />
                          Médiathèque
                        </Button>
                        <label className="cursor-pointer">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                            asChild
                          >
                            <span>
                              <FiUpload className="h-4 w-4" />
                              Uploader
                            </span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  toast.loading('Upload en cours...');
                                  const formDataUpload = new FormData();
                                  formDataUpload.append('file', file);
                                  const response = await mediaAPI.upload(formDataUpload);
                                  const uploadedUrl = response.data.url?.startsWith('http') 
                                    ? response.data.url 
                                    : `${API_URL}${response.data.url}`;
                                  setFormData(prev => ({ ...prev, images: [uploadedUrl] }));
                                  toast.dismiss();
                                  toast.success('Image uploadée');
                                } catch (err) {
                                  toast.dismiss();
                                  toast.error('Erreur lors de l\'upload');
                                }
                              }
                            }}
                          />
                        </label>
                      </div>
                      
                      {/* URL manuelle (fallback) */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">ou</span>
                        <Input
                          value={formData.images?.[0] || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, images: [e.target.value] }))}
                          placeholder="Coller une URL d'image..."
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                    />
                    <Label>Actif</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_featured}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                    />
                    <Label>Produit vedette</Label>
                  </div>
                </div>
              </TabsContent>

              {/* Variations Tab */}
              <TabsContent value="variations" className="space-y-4 mt-0">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-500">
                    Ajoutez des variations (ex: durées différentes avec prix)
                  </p>
                  <Button variant="outline" size="sm" onClick={addVariation}>
                    <FiPlus className="mr-1" /> Ajouter
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.variations.map((variation, index) => (
                    <Card key={variation.id} className="p-4">
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label className="text-xs">Nom</Label>
                          <Input
                            value={variation.name}
                            onChange={(e) => updateVariation(index, 'name', e.target.value)}
                            placeholder="3 heures"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Prix (€)</Label>
                          <Input
                            type="number"
                            value={variation.price}
                            onChange={(e) => updateVariation(index, 'price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Durée (h)</Label>
                          <Input
                            type="number"
                            value={variation.duration_hours || ''}
                            onChange={(e) => updateVariation(index, 'duration_hours', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="Illimitée"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Switch
                            checked={variation.is_active}
                            onCheckedChange={(v) => updateVariation(index, 'is_active', v)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => removeVariation(index)}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                
                {formData.variations.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    Aucune variation. Cliquez sur "Ajouter" pour créer des variations de durée.
                  </div>
                )}
              </TabsContent>

              {/* Options Tab */}
              <TabsContent value="options" className="space-y-4 mt-0">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-500">
                    Options supplémentaires (ex: livre d'or, accessoires)
                  </p>
                  <Button variant="outline" size="sm" onClick={addOption}>
                    <FiPlus className="mr-1" /> Ajouter
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.options.map((option, index) => (
                    <Card key={option.id} className="p-4">
                      <div className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-4">
                          <Label className="text-xs">Nom</Label>
                          <Input
                            value={option.name}
                            onChange={(e) => updateOption(index, 'name', e.target.value)}
                            placeholder="Livre d'or"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Prix (€)</Label>
                          <Input
                            type="number"
                            value={option.price}
                            onChange={(e) => updateOption(index, 'price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={option.description || ''}
                            onChange={(e) => updateOption(index, 'description', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                          <Switch
                            checked={option.is_included}
                            onCheckedChange={(v) => updateOption(index, 'is_included', v)}
                          />
                          <span className="text-xs">Inclus</span>
                        </div>
                        <div className="col-span-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => removeOption(index)}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Formulas Tab - Product Bundles with Discounts */}
              <TabsContent value="formulas" className="space-y-4 mt-0">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-500">
                      Formules combinées avec réductions
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Combinez plusieurs produits avec une réduction en % ou €
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      formulas: [...prev.formulas, {
                        id: `formula-${Date.now()}`,
                        name: '',
                        description: '',
                        products: [],
                        discount_type: 'percentage',
                        discount_value: 10,
                        final_price: null,
                        is_active: true,
                        image: ''
                      }]
                    }));
                  }}>
                    <FiPlus className="mr-1" /> Nouvelle formule
                  </Button>
                </div>
                
                {formData.formulas.length === 0 ? (
                  <Card className="p-8 text-center">
                    <FiPackage className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">Aucune formule</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Créez des combinaisons de produits avec des remises attractives
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {formData.formulas.map((formula, index) => (
                      <Card key={formula.id} className="p-5">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1 grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Nom de la formule *</Label>
                              <Input
                                value={formula.name}
                                onChange={(e) => {
                                  const newFormulas = [...formData.formulas];
                                  newFormulas[index].name = e.target.value;
                                  setFormData(prev => ({ ...prev, formulas: newFormulas }));
                                }}
                                placeholder="Formule Mariage Premium"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Description</Label>
                              <Input
                                value={formula.description}
                                onChange={(e) => {
                                  const newFormulas = [...formData.formulas];
                                  newFormulas[index].description = e.target.value;
                                  setFormData(prev => ({ ...prev, formulas: newFormulas }));
                                }}
                                placeholder="PhotoBooth + VideoBooth 360°"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Switch
                              checked={formula.is_active}
                              onCheckedChange={(checked) => {
                                const newFormulas = [...formData.formulas];
                                newFormulas[index].is_active = checked;
                                setFormData(prev => ({ ...prev, formulas: newFormulas }));
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  formulas: prev.formulas.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Products in formula */}
                        <div className="mb-4">
                          <Label className="text-xs mb-2 block">Produits inclus</Label>
                          <div className="flex flex-wrap gap-2">
                            {formula.products.map((prod, prodIndex) => (
                              <Badge key={prodIndex} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                                {prod.product_name}
                                {prod.variation_name && ` (${prod.variation_name})`}
                                <button
                                  onClick={() => {
                                    const newFormulas = [...formData.formulas];
                                    newFormulas[index].products = newFormulas[index].products.filter((_, i) => i !== prodIndex);
                                    setFormData(prev => ({ ...prev, formulas: newFormulas }));
                                  }}
                                  className="ml-1 text-slate-400 hover:text-red-500"
                                >
                                  <FiX className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7"
                              onClick={() => {
                                // Add current product to formula
                                if (formData.name) {
                                  const newFormulas = [...formData.formulas];
                                  const existingIds = newFormulas[index].products.map(p => p.product_id);
                                  if (!existingIds.includes(selectedProduct?.id || 'current')) {
                                    newFormulas[index].products.push({
                                      product_id: selectedProduct?.id || 'current',
                                      product_name: formData.name,
                                      variation_id: null,
                                      variation_name: null
                                    });
                                    setFormData(prev => ({ ...prev, formulas: newFormulas }));
                                  }
                                }
                              }}
                            >
                              <FiPlus className="h-3 w-3 mr-1" /> Ajouter produit actuel
                            </Button>
                          </div>
                          {formula.products.length === 0 && (
                            <p className="text-xs text-amber-600 mt-2">
                              Ajoutez des produits pour créer la formule
                            </p>
                          )}
                        </div>
                        
                        {/* Discount configuration */}
                        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-100">
                          <div>
                            <Label className="text-xs">Type de réduction</Label>
                            <select
                              className="w-full border rounded-md p-2 text-sm"
                              value={formula.discount_type}
                              onChange={(e) => {
                                const newFormulas = [...formData.formulas];
                                newFormulas[index].discount_type = e.target.value;
                                setFormData(prev => ({ ...prev, formulas: newFormulas }));
                              }}
                            >
                              <option value="percentage">Pourcentage (%)</option>
                              <option value="fixed">Montant fixe (€)</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">
                              Valeur de la réduction {formula.discount_type === 'percentage' ? '(%)' : '(€)'}
                            </Label>
                            <Input
                              type="number"
                              value={formula.discount_value}
                              onChange={(e) => {
                                const newFormulas = [...formData.formulas];
                                newFormulas[index].discount_value = parseFloat(e.target.value) || 0;
                                setFormData(prev => ({ ...prev, formulas: newFormulas }));
                              }}
                              placeholder={formula.discount_type === 'percentage' ? '10' : '50'}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Prix final (optionnel)</Label>
                            <Input
                              type="number"
                              value={formula.final_price || ''}
                              onChange={(e) => {
                                const newFormulas = [...formData.formulas];
                                newFormulas[index].final_price = e.target.value ? parseFloat(e.target.value) : null;
                                setFormData(prev => ({ ...prev, formulas: newFormulas }));
                              }}
                              placeholder="Calculé auto."
                            />
                            <p className="text-xs text-slate-400 mt-1">
                              Laissez vide pour calcul automatique
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Form Tab */}
              <TabsContent value="form" className="space-y-4 mt-0">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-500">
                    Champs du formulaire de réservation
                  </p>
                  <Button variant="outline" size="sm" onClick={addFormField}>
                    <FiPlus className="mr-1" /> Ajouter
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.booking_form_fields.map((field, index) => (
                    <Card key={field.id} className="p-4">
                      <div className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-3">
                          <Label className="text-xs">Type</Label>
                          <select
                            className="w-full border rounded-md p-2 text-sm"
                            value={field.field_type}
                            onChange={(e) => updateFormField(index, 'field_type', e.target.value)}
                          >
                            {FIELD_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={field.label}
                            onChange={(e) => updateFormField(index, 'label', e.target.value)}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Placeholder</Label>
                          <Input
                            value={field.placeholder || ''}
                            onChange={(e) => updateFormField(index, 'placeholder', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                          <Switch
                            checked={field.required}
                            onCheckedChange={(v) => updateFormField(index, 'required', v)}
                          />
                          <span className="text-xs">Requis</span>
                        </div>
                        <div className="col-span-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => removeFormField(index)}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {field.field_type === 'select' && (
                        <div className="mt-2">
                          <Label className="text-xs">Options (séparées par des virgules)</Label>
                          <Input
                            value={(field.options || []).join(', ')}
                            onChange={(e) => updateFormField(index, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            placeholder="Option 1, Option 2, Option 3"
                          />
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Deposit Tab */}
              <TabsContent value="deposit" className="space-y-6 mt-0">
                <Card className="p-4">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FiPercent className="h-4 w-4" /> Configuration de l'acompte
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.deposit_config.deposit_required}
                        onCheckedChange={(v) => setFormData(prev => ({
                          ...prev,
                          deposit_config: { ...prev.deposit_config, deposit_required: v }
                        }))}
                      />
                      <Label>Acompte obligatoire</Label>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type d'acompte</Label>
                        <select
                          className="w-full border rounded-md p-2"
                          value={formData.deposit_config.deposit_type}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            deposit_config: { ...prev.deposit_config, deposit_type: e.target.value }
                          }))}
                        >
                          <option value="percentage">Pourcentage</option>
                          <option value="fixed">Montant fixe</option>
                        </select>
                      </div>
                      <div>
                        <Label>
                          {formData.deposit_config.deposit_type === 'percentage' ? 'Pourcentage (%)' : 'Montant (€)'}
                        </Label>
                        <Input
                          type="number"
                          value={formData.deposit_config.deposit_value}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            deposit_config: { ...prev.deposit_config, deposit_value: parseFloat(e.target.value) || 0 }
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FiCreditCard className="h-4 w-4" /> Moyens de paiement acceptés
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🏦</span>
                        <div>
                          <p className="font-medium">Virement bancaire</p>
                          <p className="text-xs text-slate-500">Paiement par virement</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.payment_config.bank_transfer_enabled}
                        onCheckedChange={(v) => setFormData(prev => ({
                          ...prev,
                          payment_config: { ...prev.payment_config, bank_transfer_enabled: v }
                        }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💳</span>
                        <div>
                          <p className="font-medium">Carte bancaire (MyPOS)</p>
                          <p className="text-xs text-slate-500">Paiement en ligne sécurisé</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.payment_config.mypos_enabled}
                        onCheckedChange={(v) => setFormData(prev => ({
                          ...prev,
                          payment_config: { ...prev.payment_config, mypos_enabled: v }
                        }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💵</span>
                        <div>
                          <p className="font-medium">Espèces</p>
                          <p className="text-xs text-slate-500">Paiement en main propre</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.payment_config.cash_enabled}
                        onCheckedChange={(v) => setFormData(prev => ({
                          ...prev,
                          payment_config: { ...prev.payment_config, cash_enabled: v }
                        }))}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label>Délai de paiement après acceptation (jours)</Label>
                    <Input
                      type="number"
                      value={formData.payment_config.payment_deadline_days}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        payment_config: { ...prev.payment_config, payment_deadline_days: parseInt(e.target.value) || 7 }
                      }))}
                    />
                  </div>
                </Card>
              </TabsContent>

              {/* Conditions Tab */}
              <TabsContent value="conditions" className="space-y-4 mt-0">
                <Card className="p-4">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FiCalendar className="h-4 w-4" /> Conditions de réservation
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Délai minimum (jours avant)</Label>
                      <Input
                        type="number"
                        value={formData.conditions.min_advance_days}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          conditions: { ...prev.conditions, min_advance_days: parseInt(e.target.value) || 1 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Délai maximum (jours avant)</Label>
                      <Input
                        type="number"
                        value={formData.conditions.max_advance_days}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          conditions: { ...prev.conditions, max_advance_days: parseInt(e.target.value) || 365 }
                        }))}
                      />
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FiTruck className="h-4 w-4" /> Frais de livraison
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Rayon de livraison gratuite (km)</Label>
                      <Input
                        type="number"
                        value={formData.conditions.free_delivery_radius_km}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          conditions: { ...prev.conditions, free_delivery_radius_km: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Frais par km supplémentaire (€)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.conditions.delivery_fee_per_km}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          conditions: { ...prev.conditions, delivery_fee_per_km: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4">
                  <h4 className="font-medium mb-4">Jours disponibles</h4>
                  <div className="flex flex-wrap gap-2">
                    {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day, index) => (
                      <button
                        key={day}
                        onClick={() => {
                          const days = formData.conditions.available_days || [];
                          const newDays = days.includes(index)
                            ? days.filter(d => d !== index)
                            : [...days, index];
                          setFormData(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, available_days: newDays }
                          }));
                        }}
                        className={`px-4 py-2 rounded-lg border transition-colors ${
                          (formData.conditions.available_days || []).includes(index)
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-slate-300 hover:border-indigo-300'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </Card>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {saving ? (
                <>
                  <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <FiSave className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={(open) => {
        setShowMediaPicker(open);
        if (open) loadMediaLibrary();
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiImage className="h-5 w-5" />
              Choisir une image de la médiathèque
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingMedia ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : mediaLibrary.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FiImage className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucune image dans la médiathèque</p>
                <p className="text-sm mt-2">Uploadez des images depuis la page Médiathèque</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {mediaLibrary.map((media) => {
                  const mediaUrl = media.url || media.file_url;
                  const displayUrl = mediaUrl?.startsWith('http') ? mediaUrl : `${API_URL}${mediaUrl}`;
                  return (
                    <div
                      key={media.id}
                      onClick={() => selectMediaForProduct(media)}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-indigo-500 transition-all group bg-slate-100"
                    >
                      <img
                        src={displayUrl}
                        alt={media.name || media.original_filename || 'Image'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <FiCheck className="h-8 w-8 text-white" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate">{media.name || media.original_filename}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowMediaPicker(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
